import {XMarkIcon} from '@heroicons/react/20/solid';
import classNames from 'classnames';
import React, {ReactElement, useMemo} from 'react';

import {Spinner} from '../../../../resources/Icons';
import Row from '../../../common/Row';
import {MarketplaceCropType, MarketplaceServiceType, ProductSearchParams} from '../../../query/graphql';
import {formatCoordinates} from '../../decision/util';
import {SortOrder, SupplierBasic} from '../../marketplace/types';
import {firstValue, proximityLabel} from '../../marketplace/util';
import Tag from '../Tag';
import SortDropdown from './SortDropdown';

type Props = {
  params: ProductSearchParams;
  total?: number;
  loading: boolean;
  onSortOrderChange: (orderKey: keyof typeof SortOrder) => void;
  onTagClick: (key: keyof ProductSearchParams | (keyof ProductSearchParams)[]) => void;
  className?: string;
  approvedSuppliers: SupplierBasic[];
  allServices: MarketplaceServiceType[];
  allCrops: MarketplaceCropType[];
};

const SearchHeader = ({
  params,
  total,
  loading,
  onSortOrderChange,
  onTagClick,
  className,
  approvedSuppliers,
  allServices,
  allCrops,
}: Props): ReactElement => {
  const {location, postcode, proximity, query, serviceTypes, cropTypes, suppliers} = params;

  const tags = useMemo(() => {
    const result: {text: string; key: keyof ProductSearchParams | (keyof ProductSearchParams)[]}[] = [];
    if (query) {
      result.push({text: `"${query}"`, key: 'query'});
    }
    if (postcode) {
      result.push({text: postcode.toUpperCase(), key: ['postcode', 'location']});
    } else if (location) {
      result.push({
        text: formatCoordinates(location, 4),
        key: ['postcode', 'location'],
      });
    }
    if ((postcode || location) && proximity) {
      result.push({text: proximityLabel(proximity), key: 'proximity'});
    }
    if (serviceTypes) {
      const firstName = allServices.find(s => s.id === firstValue(serviceTypes))?.name;
      const count = serviceTypes.length > 1 ? ` +${serviceTypes.length - 1}` : '';
      result.push({
        text: `${firstName}${count}`,
        key: 'serviceTypes',
      });
    }
    if (cropTypes) {
      const firstName = allCrops.find(s => s.id === firstValue(cropTypes))?.name;
      const count = cropTypes.length > 1 ? ` +${cropTypes.length - 1}` : '';
      result.push({
        text: `${firstName}${count}`,
        key: 'cropTypes',
      });
    }
    if (suppliers) {
      const firstName = approvedSuppliers.find(s => s.slug === firstValue(suppliers))?.name;

      result.push({
        text: `${firstName}${Array.isArray(suppliers) && suppliers.length > 1 ? ` +${suppliers.length - 1}` : ''}`,
        key: 'suppliers',
      });
    }

    return result;
  }, [
    allCrops,
    allServices,
    approvedSuppliers,
    cropTypes,
    location,
    postcode,
    proximity,
    query,
    serviceTypes,
    suppliers,
  ]);

  const sortKeys: (keyof typeof SortOrder)[] = ['name', '-name'];

  return (
    <Row className={classNames('min-w-0 min-h-[4.6rem] items-stretch gap-xs text-sm tablet:flex-wrap', className)}>
      <Row className={'min-w-0 grow items-stretch gap-xs'}>
        {!!tags.length && (
          <Row
            className={
              'pr-xs items-center space-x-3 overflow-auto min-w-0 basis-0 grow shrink border-r-[1px] border-r-gray-100'
            }
          >
            {tags.map((tag, idx) => (
              <Tag key={idx} className={'!text-sm'}>
                <Row className={'items-center gap-3 cursor-pointer group'} onClick={() => onTagClick(tag.key)}>
                  {tag.text}
                  {<XMarkIcon width={20} height={20} className={'text-primary-800 group-hover:text-red-200'} />}
                </Row>
              </Tag>
            ))}
          </Row>
        )}
      </Row>
      {loading && <Spinner className={'self-center'} />}
      {!loading && total !== undefined && (
        <Row className={'items-center gap-x-xs mobile:flex-wrap'}>
          <span className={'whitespace-nowrap'}>
            {total} {total === 1 ? 'result' : 'results'}
          </span>
          <div className={'w-px self-stretch my-[1rem] bg-gray-100'} />
          <SortDropdown order={params.sort ?? undefined} options={sortKeys} onChange={onSortOrderChange} />
        </Row>
      )}
    </Row>
  );
};

export default SearchHeader;
